import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../Layout/AdminLayout";
import API from "../../Api/Api";
import endpoints from "../../Api/Endpoints";
import { showAlert, showConfirm } from "../../feedback/feedbackApi";
import "./Gallery.scss";

function formatBytes(size) {
  const n = Number(size) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyDelete, setBusyDelete] = useState(false);
  const [prefix, setPrefix] = useState("");
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());

  const selectedCount = selectedKeys.size;

  const allChecked = useMemo(() => {
    if (items.length === 0) return false;
    return items.every((x) => selectedKeys.has(x.key));
  }, [items, selectedKeys]);

  const fetchObjects = async () => {
    setLoading(true);
    try {
      const res = await API.get(endpoints.ADMIN_GALLERY_OBJECTS, {
        params: { limit: 500, prefix: prefix.trim() || undefined },
      });
      const list = Array.isArray(res?.data?.data?.items) ? res.data.data.items : [];
      setItems(list);
      setSelectedKeys((prev) => {
        const next = new Set();
        list.forEach((x) => {
          if (prev.has(x.key)) next.add(x.key);
        });
        return next;
      });
    } catch (e) {
      setItems([]);
      showAlert(e?.response?.data?.message || "Could not load S3 gallery.", {
        variant: "error",
        title: "Gallery",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchObjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSelectAll = () => {
    setSelectedKeys((prev) => {
      if (items.length > 0 && items.every((x) => prev.has(x.key))) return new Set();
      return new Set(items.map((x) => x.key));
    });
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    const keys = Array.from(selectedKeys);
    if (keys.length === 0) {
      showAlert("Please select at least one file.", {
        variant: "warning",
        title: "Gallery",
      });
      return;
    }

    const ok = await showConfirm(`Delete ${keys.length} selected file(s) from S3?`, {
      title: "Delete selected",
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;

    setBusyDelete(true);
    try {
      const res = await API.delete(endpoints.ADMIN_GALLERY_OBJECTS, {
        data: { keys },
      });
      showAlert(res?.data?.message || "Selected files deleted.", {
        variant: "success",
        title: "Gallery",
      });
      setSelectedKeys(new Set());
      await fetchObjects();
    } catch (e) {
      showAlert(e?.response?.data?.message || "Could not delete selected files.", {
        variant: "error",
        title: "Gallery",
      });
    } finally {
      setBusyDelete(false);
    }
  };

  const handleDeleteAll = async () => {
    const ok = await showConfirm(
      `Delete ALL ${items.length} file(s)${prefix.trim() ? ` under "${prefix.trim()}"` : ""} from S3?`,
      {
        title: "Delete all S3 files",
        confirmText: "Delete all",
        danger: true,
      }
    );
    if (!ok) return;

    setBusyDelete(true);
    try {
      const res = await API.delete(endpoints.ADMIN_GALLERY_OBJECTS_DELETE_ALL, {
        data: { prefix: prefix.trim() || "" },
      });
      showAlert(res?.data?.message || "All files deleted.", {
        variant: "success",
        title: "Gallery",
      });
      setSelectedKeys(new Set());
      await fetchObjects();
    } catch (e) {
      showAlert(e?.response?.data?.message || "Could not delete all files.", {
        variant: "error",
        title: "Gallery",
      });
    } finally {
      setBusyDelete(false);
    }
  };

  return (
    <AdminLayout>
      <div className="galleryPage">
        <div className="galleryCard">
          <div className="galleryTop">
            <h2>S3 Gallery</h2>
            <p>Select files and delete selected or delete all.</p>
          </div>

          <div className="galleryTools">
            <input
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              placeholder="Optional prefix filter (e.g. prescriptions/)"
            />
            <button type="button" onClick={fetchObjects} disabled={loading || busyDelete}>
              {loading ? "Loading..." : "Load"}
            </button>
            <button type="button" onClick={toggleSelectAll} disabled={items.length === 0 || loading || busyDelete}>
              {allChecked ? "Unselect all" : "Select all"}
            </button>
            <button type="button" className="danger" onClick={handleDeleteSelected} disabled={busyDelete || selectedCount === 0}>
              Delete selected ({selectedCount})
            </button>
            <button type="button" className="danger" onClick={handleDeleteAll} disabled={busyDelete || items.length === 0}>
              Delete all ({items.length})
            </button>
          </div>

          <div className="galleryTableWrap">
            <table className="galleryTable">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleSelectAll}
                      disabled={items.length === 0}
                    />
                  </th>
                  <th>Key</th>
                  <th>Size</th>
                  <th>Last modified</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="empty">
                      {loading ? "Loading..." : "No files found."}
                    </td>
                  </tr>
                ) : (
                  items.map((x) => (
                    <tr key={x.key}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedKeys.has(x.key)}
                          onChange={() => toggleOne(x.key)}
                        />
                      </td>
                      <td className="mono">{x.key}</td>
                      <td>{formatBytes(x.size)}</td>
                      <td>{x.lastModified ? new Date(x.lastModified).toLocaleString() : "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
